import { useRef, useState, useEffect } from 'react'
import { DayPicker } from 'react-day-picker'
import { usePopper } from 'react-popper'
import cn from 'classnames'
import s from './WeekPicker.scss'
import moment from 'moment'
import { OutsideClickContainer } from 'components/ideal-core-ui'

moment.updateLocale('en', {
  week: {
    dow: 0
  }
})

const getWeekDays = weekStart => {
  const days = [weekStart]
  for (let i = 1; i < 7; i += 1) {
    days.push(moment(weekStart).add(i, 'days').toDate())
  }
  return days
}

const getCurrentWeekDays = () => {
  const weekStart = moment().startOf('week').toDate()
  return getWeekDays(weekStart)
}

const getWeekRange = date => {
  return {
    from: moment(date).startOf('week').toDate(),
    to: moment(date).endOf('week').toDate()
  }
}

const WeekPicker = ({
  disabled,
  className,
  disabledDays,
  onChange,
  weekStartsOn = 0,
  value,
  cb
}) => {
  const [selectedDays, setSelectedDays] = useState(value ?? getCurrentWeekDays())
  const [hoverRange, setHoverRange] = useState(null)
  const [isPopperOpen, setIsPopperOpen] = useState(false)

  const popperRef = useRef(null)
  const [popperElement, setPopperElement] = useState(null)

  useEffect(() => {
    moment.updateLocale('en', {
      week: {
        dow: weekStartsOn
      }
    })

    setSelectedDays(value ?? getCurrentWeekDays())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartsOn])

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start'
  })

  const closePopper = () => {
    setIsPopperOpen(false)
  }

  const handleClick = () => {
    !disabled && setIsPopperOpen(true)
  }

  const handleDayChange = date => {
    const range = getWeekDays(getWeekRange(date).from)
    setSelectedDays(range)
    closePopper()
    onChange(getWeekRange(date))

    if (cb) cb(range)
  }

  const handleDayEnter = date => {
    if (moment(date).isBetween(selectedDays[0], selectedDays[6])) return
    setHoverRange(getWeekRange(date))
  }

  const handleDayLeave = () => {
    setHoverRange(null)
  }

  return (
    <div className={cn(s.weekPickerWrapper, className)}>
      <div ref={popperRef} onClick={handleClick} className={s.date}>
        {selectedDays.length === 7 ? (
          `${moment(selectedDays[0]).format('LL').split(',')[0]} – ${moment(selectedDays[6]).format(
            'LL'
          )}`
        ) : (
          <i className={s.placeholder}>Please select a week</i>
        )}
      </div>
      {isPopperOpen && (
        <OutsideClickContainer styles={{ height: 'initial' }} closeComponent={closePopper}>
          <div
            tabIndex={-1}
            style={popper.styles.popper}
            {...popper.attributes.popper}
            ref={setPopperElement}
            className={s.popper}
          >
            <DayPicker
              captionLayout="dropdown-buttons"
              fromYear={2000}
              toYear={2100}
              initialFocus={isPopperOpen}
              showOutsideDays
              modifiers={{ hovered: hoverRange }}
              modifiersStyles={{
                hovered: { backgroundColor: '#e7edff', color: '#00000' }
              }}
              mode="single"
              defaultMonth={selectedDays ? moment(selectedDays[0]).toDate() : moment().toDate()}
              selected={selectedDays}
              className={className}
              disabled={disabledDays}
              onDayClick={handleDayChange}
              onDayMouseEnter={handleDayEnter}
              onDayMouseLeave={handleDayLeave}
              weekStartsOn={weekStartsOn}
            />
          </div>
        </OutsideClickContainer>
      )}
    </div>
  )
}

export default WeekPicker
